import { Schema as S } from "effect";

import type { RecordSummary } from "../api/schemas/records";

/**
 * Pure rules behind the capture page (U4): payload decoding, the canonical
 * LinkedIn URL (mirrors the server's `core/linkedinUrl.ts`), the conservative
 * name match (KTD4), and the Link / Sync overlays (KTD6). No I/O.
 */

/** A readable capture (R4): name and canonical URL are required; the rest may be absent. */
export const Capture = S.Struct({
	name: S.String,
	url: S.String,
	headline: S.optionalKey(S.String),
	position: S.optionalKey(S.String),
	organisation: S.optionalKey(S.String),
	location: S.optionalKey(S.String),
	/** Sent to the server only (KTD3); never an image `src` on the page. */
	photo: S.optionalKey(S.String),
});
export type Capture = typeof Capture.Type;

/** The attribute keys LinkedIn owns (R8); they match the CSV import vocabulary. */
const LINKEDIN_FIELDS = [
	"headline",
	"position",
	"organisation",
	"location",
] as const;

const LINKEDIN_HOST = /^(?:[a-z0-9-]+\.)?linkedin\.com$/;

/** KTD10: `https://www.linkedin.com/in/<slug>`, lowercase, no query, fragment, or trailing slash. */
export const canonicalLinkedinUrl = (raw: string): string | undefined => {
	const trimmed = raw.trim();
	if (trimmed === "") return undefined;
	const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed)
		? trimmed
		: `https://${trimmed}`;
	let url: URL;
	try {
		url = new URL(withScheme);
	} catch {
		return undefined;
	}
	if (url.protocol !== "https:" && url.protocol !== "http:") return undefined;
	if (!LINKEDIN_HOST.test(url.hostname.toLowerCase())) return undefined;
	const match = url.pathname.match(/^\/in\/([^/]+)\/?$/);
	if (!match) return undefined;
	let slug: string;
	try {
		slug = decodeURIComponent(match[1] ?? "").toLowerCase();
	} catch {
		// A malformed percent escape is not a profile URL; the capture reads as unreadable.
		return undefined;
	}
	return `https://www.linkedin.com/in/${encodeURIComponent(slug)}`;
};

/** Mirrors the server's `normalizeName` (core/importMatch.ts). */
export const normalizeName = (raw: string): string =>
	raw.trim().replace(/\s+/g, " ").toLowerCase();

const photoUrl = (raw: string | undefined): string | undefined => {
	if (raw === undefined) return undefined;
	try {
		const url = new URL(raw);
		return url.protocol === "https:" && url.hostname === "media.licdn.com"
			? raw
			: undefined;
	} catch {
		return undefined;
	}
};

const present = (params: URLSearchParams, key: string): string | undefined => {
	const value = params.get(key)?.trim();
	return value ? value : undefined;
};

const optional = <K extends string>(key: K, value: string | undefined) =>
	value === undefined ? {} : ({ [key]: value } as Record<K, string>);

/**
 * Fragment body → capture, or `undefined` when unreadable (KTD3, R4): an
 * `error` field, or no name or LinkedIn `/in/` URL. Field names are
 * additive-only, so an older extension's payload keeps decoding.
 */
export const decodePayload = (raw: string): Capture | undefined => {
	const params = new URLSearchParams(raw.startsWith("#") ? raw.slice(1) : raw);
	if (params.has("error")) return undefined;
	const name = present(params, "name");
	const url = canonicalLinkedinUrl(params.get("url") ?? "");
	if (name === undefined || url === undefined) return undefined;
	return {
		name,
		url,
		...optional("headline", present(params, "headline")),
		...optional("position", present(params, "position")),
		...optional("organisation", present(params, "organisation")),
		...optional("location", present(params, "location")),
		...optional("photo", photoUrl(present(params, "photo"))),
	};
};

/** KTD4: exactly one exact-name row whose LinkedIn identity is absent or is this profile; else nothing. */
export const selectByName = (
	capture: Capture,
	rows: ReadonlyArray<RecordSummary>,
): RecordSummary | undefined => {
	const key = normalizeName(capture.name);
	const matches = rows.filter((row) => {
		const identity = row.externalId?.linkedin;
		return (
			normalizeName(row.name) === key &&
			(identity === undefined || canonicalLinkedinUrl(identity) === capture.url)
		);
	});
	return matches.length === 1 ? matches[0] : undefined;
};

/** R6: Link stores only the canonical identity. */
export const linkOverlay = (
	record: RecordSummary,
	capture: Capture,
): RecordSummary => ({
	...record,
	externalId: { ...record.externalId, linkedin: capture.url },
});

const capturedAttributes = (capture: Capture): Record<string, string> =>
	Object.fromEntries(
		LINKEDIN_FIELDS.flatMap((key) => {
			const value = capture[key];
			return value === undefined ? [] : [[key, value] as const];
		}),
	);

/** R8/R10: LinkedIn wins its own fields; a field the capture lacks is left alone; everything else is as fetched. */
export const syncOverlay = (
	record: RecordSummary,
	capture: Capture,
): RecordSummary => ({
	...linkOverlay(record, capture),
	attributes: { ...record.attributes, ...capturedAttributes(capture) },
});

/** R11: what a Sync wrote, for the confirmation line. */
export const syncedFields = (capture: Capture): ReadonlyArray<string> => [
	"url",
	...LINKEDIN_FIELDS.filter((key) => capture[key] !== undefined),
];

/** R7: the `addRecord` body for "Create new", shaped like the Records page's create. */
export const createBody = (capture: Capture, timestamp: string) => ({
	id: "",
	circleId: "",
	type: "person" as const,
	name: capture.name,
	description: "",
	tags: [] as ReadonlyArray<string>,
	avatars: [] as ReadonlyArray<string>,
	attributes: capturedAttributes(capture),
	externalId: { linkedin: capture.url },
	timestamp,
});
